import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye } from 'lucide-react';
import { useData } from '../../context/DataContext';

const PRESENCE_COLORS = [
  'bg-indigo-500', 'bg-cyan-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-rose-500', 'bg-violet-500',
];

function colorForUser(userId) {
  let hash = 0;
  for (let i = 0; i < (userId || '').length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
  }
  return PRESENCE_COLORS[Math.abs(hash) % PRESENCE_COLORS.length];
}

/**
 * Shows who else is viewing a task/risk. Auto-joins/leaves on mount/unmount.
 * Usage: <PresenceAwareness itemType="task" itemId={task.id} />
 */
export const PresenceAwareness = ({ itemType, itemId }) => {
  const { presence, sendPresence } = useData();

  useEffect(() => {
    if (!itemId) return;
    sendPresence('join', itemType, itemId);
    return () => sendPresence('leave', itemType, itemId);
  }, [itemType, itemId, sendPresence]);

  const key = `${itemType}:${itemId}`;
  const viewers = presence[key] || [];
  const currentUserId = JSON.parse(localStorage.getItem('d3_user') || '{}').id;
  const others = viewers.filter(v => v.userId !== currentUserId);

  if (others.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5" data-testid="presence-awareness">
      <Eye className="w-3.5 h-3.5 text-gray-500" />
      <div className="flex -space-x-2">
        <AnimatePresence>
          {others.slice(0, 5).map((v) => (
            <motion.div
              key={v.userId}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className={`w-6 h-6 rounded-full ${colorForUser(v.userId)} flex items-center justify-center border-2 border-[#0d1221] cursor-default`}
              title={`${v.name} is viewing`}
              data-testid={`presence-avatar-${v.userId}`}
            >
              <span className="text-[9px] font-bold text-white">{v.initials}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <span className="text-[11px] text-gray-500">
        {others.length === 1
          ? `${others[0].name.split(' ')[0]} is viewing`
          : `${others.length} viewing`}
      </span>
    </div>
  );
};

/**
 * Compact presence badge for card/list items (shows dot + count).
 * Usage: <PresenceBadge itemType="task" itemId={task.id} />
 */
export const PresenceBadge = ({ itemType, itemId }) => {
  const { presence } = useData();
  const key = `${itemType}:${itemId}`;
  const viewers = presence[key] || [];
  const currentUserId = JSON.parse(localStorage.getItem('d3_user') || '{}').id;
  const others = viewers.filter(v => v.userId !== currentUserId);

  if (others.length === 0) return null;

  return (
    <div
      className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/20"
      title={others.map(v => v.name).join(', ')}
      data-testid={`presence-badge-${itemType}-${itemId}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      <span className="text-[10px] font-medium text-emerald-400">
        {others.length}
      </span>
    </div>
  );
};

export default PresenceAwareness;
